"use client"
import React, { useEffect, useState } from "react"
import Maps from "@/features/dashboard/components/maps/map"
import MapSearchHomeInput from "@/features/homeowners/components/map-search-home-input"
import { House } from "./maps/houses"
import { useLightweightHomes, LightweightHomesResponse } from "@/features/homeowners/hooks/lightweight-homes.hook"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { useSession } from "@/features/auth/context/session.context"
import {
  shouldMaskHomeownerData,
  maskHomeownerLastName,
  maskHomeownerAddress,
} from "@/shared/lib/utils/homeowner-privacy.utils"
import { getDisplayAddressValue, isUnknownLocationValue } from "@/features/homeowners/lib/unknown-location"

interface MapsTabProps {
    homeId?: string
}

function MapsTab({ homeId }: MapsTabProps) {
    const { regionId, isLoading: regionLoading, region } = useRegionId()
    const { session } = useSession()
    const [selectedHouse, setSelectedHouse] = useState<House | null>(null)
    const [houses, setHouses] = useState<House[]>([]) // store API houses
    // Only fetch when regionId is loaded to avoid stale data
    const shouldFetchHomes = !regionLoading && regionId !== null && regionId !== undefined
    const { data, isLoading } = useLightweightHomes(
        shouldFetchHomes ? regionId ?? undefined : undefined,
        { enabled: shouldFetchHomes }
    )

    const fallbackCenter = React.useMemo(() => {
        if (!region) return undefined
        const lat = region.baseLatitude
        const lng = region.baseLongitude
        if (lat === null || lat === undefined || lng === null || lng === undefined) return undefined
        const parsedLat = typeof lat === 'string' ? parseFloat(lat) : lat
        const parsedLng = typeof lng === 'string' ? parseFloat(lng) : lng
        if (Number.isNaN(parsedLat) || Number.isNaN(parsedLng)) return undefined
        return { lat: parsedLat, lng: parsedLng }
    }, [region])

    const fallbackZoom = React.useMemo(() => {
        if (!region?.defaultMapZoom && region?.defaultMapZoom !== 0) return 12
        const zoomValue = typeof region.defaultMapZoom === 'string'
            ? parseInt(region.defaultMapZoom, 10)
            : region.defaultMapZoom
        return Number.isFinite(zoomValue) ? zoomValue : 12
    }, [region])

    useEffect(() => {
        try{
        if (!data) {
            return;
        }
        const response = data as LightweightHomesResponse;
        if (response.success && Array.isArray(response.data)) {
            const apiHouses: House[] = response.data
                .map((h: any, idx: number) => { 
                    const latRaw = h?.latitude
                    const lngRaw = h?.longitude
                    if (latRaw == null || lngRaw == null) return null
                    const lat = Number(latRaw)
                    const lng = Number(lngRaw)
                    if (isNaN(lat) || isNaN(lng)) return null
                    const rawName = h.homeowner?.user?.fullname || "Unknown Homeowner"
                    const mask = shouldMaskHomeownerData(session?.role, session?.sub, h.homeowner?.user?.id)
                    const displayName = mask ? maskHomeownerLastName(rawName) : rawName
                    const rawAddr = h.address || ""
                    const displayAddress = isUnknownLocationValue(rawAddr)
                      ? getDisplayAddressValue(rawAddr)
                      : (mask ? maskHomeownerAddress(h.address) : rawAddr)
                    const houseName = rawAddr ? displayAddress : `House ${idx + 1}`
                    return {
                        id: h.id?.toString() || `${idx}`,
                        homeownerId: h.homeowner?.id?.toString() || h.homeownerId?.toString(),
                        houseName,
                        position: { lat, lng },
                        homeowner: displayName,
                        address: displayAddress,
                        status: "Pending", // Default status since lightweight data doesn't include status
                        addressMasked: mask,
                    } as House
                })
                .filter((h: House | null): h is House => h !== null)
            setHouses(apiHouses)
        }
        }
        catch(error){
            console.log(error,"error")
        }
       
    }, [data, session?.role, session?.sub])

    // Set selected house when homeId is provided
    useEffect(() => {
        if (homeId && houses.length > 0) {
            const houseToSelect = houses.find(house => house.id === homeId)
            if (houseToSelect) {
                setSelectedHouse(houseToSelect)
            }
        }
    }, [homeId, houses])

    return (
        <div className="flex flex-col gap-5"> 
        <MapSearchHomeInput 
            onSelectHouse={(houseId) => {
                if (houseId) {
                    const house = houses.find(h => h.id === houseId)
                    if (house) {
                        setSelectedHouse(house)
                    }
                } else {
                    setSelectedHouse(null)
                }
            }}
        />  
         <Maps
            selectedHouse={selectedHouse}
            houses={houses}
            fallbackCenter={fallbackCenter}
            fallbackZoom={fallbackZoom}
            baseMarkerPosition={fallbackCenter ?? null}
        />
        </div>
    )
}

export default MapsTab
